// Модели разделов в памяти: одна загрузка на раздел, общая для бокового меню
// и полотна. Черновик перечитывается раз в 2,5 с, пока открыт предпросмотр
// (README §10.3 «предпросмотр вживую»), опубликованное — при открытии.

import { useEffect } from 'react'
import { create } from 'zustand'
import { ApiError } from './http'
import { sectionsApi } from './store'
import type { SectionModel } from './types'

export const DRAFT_POLL_MS = 2500

type Key = `${string}|draft` | `${string}|pub`
type Entry = { model: SectionModel | null; missing: boolean; error: string | null; json: string }

interface ModelsState {
  entries: Record<string, Entry>
  /** выбранный пункт меню / фильтр полотна по разделу */
  filter: Record<string, { node: string | null; expr: string | null }>
  load: (id: string, draft: boolean) => Promise<void>
  setFilter: (key: string, node: string | null, expr: string | null) => void
  /** действие с `confirm`, ждущее подтверждения на месте: ключ раздела → id действия */
  asking: Record<string, string | null>
  setAsking: (key: string, actionId: string | null) => void
}

export const keyOf = (id: string, draft: boolean): Key => `${id}|${draft ? 'draft' : 'pub'}`

export const useModels = create<ModelsState>()((set, get) => ({
  entries: {},
  filter: {},
  load: async (id, draft) => {
    const key = keyOf(id, draft)
    try {
      let model: SectionModel | null
      if (draft) {
        model = await sectionsApi.draft(id)
      } else {
        const full = await sectionsApi.full(id)
        model = full.section
      }
      const json = JSON.stringify(model)
      // тот же JSON — не будим рендер (опрос раз в 2,5 с)
      if (get().entries[key]?.json === json) return
      set((s) => ({ entries: { ...s.entries, [key]: { model, missing: model === null, error: null, json } } }))
    } catch (error) {
      const missing = error instanceof ApiError && error.status === 404
      const prev = get().entries[key]
      if (missing && prev?.missing) return
      set((s) => ({
        entries: {
          ...s.entries,
          [key]: { model: missing ? null : prev?.model ?? null, missing, error: missing ? null : String((error as Error).message ?? error), json: '' },
        },
      }))
    }
  },
  setFilter: (key, node, expr) => set((s) => ({ filter: { ...s.filter, [key]: { node, expr } } })),
  asking: {},
  setAsking: (key, actionId) => set((s) => ({ asking: { ...s.asking, [key]: actionId } })),
}))

/** Модель раздела; `draft` — с опросом, пока компонент смонтирован. */
export function useSectionModel(id: string | null, draft: boolean, poll = true): Entry | undefined {
  const key = id ? keyOf(id, draft) : null
  const entry = useModels((s) => (key ? s.entries[key] : undefined))
  useEffect(() => {
    if (!id) return
    const load = () => void useModels.getState().load(id, draft)
    load()
    if (!draft || !poll) return
    const t = window.setInterval(() => {
      if (document.visibilityState === 'visible') load()
    }, DRAFT_POLL_MS)
    return () => window.clearInterval(t)
  }, [id, draft, poll])
  return entry
}
